// PostHog on the sign-up (Vova 29.10.2026; in full since 03.11.2026). The sign-up is outside the cookie banner by an
// accepted risk (Vova 29.10.2026): no banner here, PostHog runs for everyone and keeps its standard state — its
// cookie on .tapfiliate.com, the same one the site's PostHog writes with the visitor's consent. So:
//   - a visitor with the site's cookie goes on in the same visitor and the same session: the visit before joins the
//     sign-up as it is, with its source and pages;
//   - a visitor without it (refused or never answered the banner on tapfiliate.com, where PostHog then runs without
//     cookies) starts here; the site passes where the visit came from in the link (tap_ref, tap_landing —
//     tapfiliate/site, src/cookie-consent/signup-links.ts) and this session takes its start from them, so its channel
//     is the visit's (Google, an ad, direct), not "Referral from tapfiliate.com". The pages before are not joined:
//     nothing ties them;
//   - once step 1 is accepted, the visitor is named after the sign-up's uuid (its hash, signupPersonId), which the
//     sign-up keeps itself, so a return to an unfinished sign-up joins too; at the end PostHog forgets the person
//     (endSignupAnalytics), as the sign-up forgets its draft.
// Event names and what each one means: engineering/analytics-events.md in tapfiliate/knowledge. Session recordings stay
// off here: passwords and payment details.
// Events go through tapfiliate.com/_relay/ (the site's worker) to PostHog's EU cloud, past ad blockers.

type Properties = Record<string, unknown>;
interface PostHog {
    init(token: string, config: Record<string, unknown>): void;
    capture(event: string, properties?: Properties, options?: Record<string, unknown>): void;
    identify(distinctId: string, userPropertiesToSet?: Properties): void;
    get_distinct_id(): string;
    reset(): void;
    set_config(config: Record<string, unknown>): void;
    _i?: unknown[];
}

// The real library, as `loaded` hands it over: what the stub does not queue.
interface LoadedPostHog extends PostHog {
    onSessionId(callback: (sessionId: string, windowId: string, reason?: SessionChange) => void): () => void;
}
type SessionChange = { noSessionId?: boolean; activityTimeout?: boolean; sessionPastMaximumLength?: boolean };
type CapturedEvent = { properties?: Properties; $set_once?: Properties } | null;
declare global {
    interface Window {
        posthog?: PostHog;
    }
}

// Each environment is a PostHog project of its own (PostHog has no environments inside a project any more): "Prod"
// (321136) for the live sign-up, "Stage" (326191) for staging. Public keys, the browser sends them with every event.
// Hosts not listed send nothing: local runs, develop.
const PROJECT_KEYS: Record<string, string> = {
    'signup2.tapfiliate.com': 'phc_wQyYcWQiZcFhSPDsMzV4rtZjAJJjKiS2Z3FGjAVSqdqu',
    'signup2.staging.tapfiliate.com': 'phc_kELUBTeumSD4D3SqKxdMfvY4kTbPdepSagWziVgpm2Fs',
};
const API_HOST = 'https://tapfiliate.com/_relay';

// Where the visit came from, as the site passed it in a sign-up address: the referrer's origin ('$direct' with none)
// and the first page.
function visitStart(search: string): Properties | undefined {
    const query = new URLSearchParams(search);
    const ref = query.get('tap_ref');
    const landing = query.get('tap_landing');
    if (!ref || !landing?.startsWith('/') || landing.startsWith('//')) return undefined;
    try {
        const from = ref === '$direct' ? undefined : new URL(ref);
        // A web page, or an app the site saw as android-app://<package> (LinkedIn, Gmail).
        if (from && !/^(https?|android-app):$/.test(from.protocol)) return undefined;
        const referringDomain = from?.hostname ?? '$direct';
        return {
            $referrer: ref,
            $referring_domain: referringDomain,
            $session_entry_referrer: ref,
            $session_entry_referring_domain: referringDomain,
            site_landing_path: new URL(landing, 'https://tapfiliate.com').pathname,
        };
    } catch {
        return undefined;
    }
}

// What PostHog knows the person by: a hash of the sign-up's uuid, not the uuid. The uuid opens the unfinished sign-up
// (every step's request carries it), and PostHog's id sits in a cookie every subdomain of tapfiliate.com can read.
async function signupPersonId(uuid: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(uuid));
    return `signup_${Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

function identifyAs(uuid: string, properties?: Properties) {
    signupPersonId(uuid)
        .then((id) => safely(() => window.posthog?.identify(id, properties)))
        .catch((error) => console.error('PostHog', error));
}

// The sign-up's own record of an unfinished sign-up (useLocalStorage, path 'default'): its uuid.
function storedSignupUuid(): string | undefined {
    try {
        return JSON.parse(localStorage.getItem('default.stepUuid') || 'null') || undefined;
    } catch {
        return undefined;
    }
}

// PostHog's loader, as PostHog publishes it (docs/onboarding/product-analytics/_snippets/js-snippet-builder.ts in
// PostHog's repository): a stub that queues calls until array.js arrives.
/* eslint-disable */
// prettier-ignore
function loadStub() {
    // @ts-ignore
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog && window.posthog.__loaded)||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}p||((p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",p.onerror=function(){p=null},(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r));var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],Object.defineProperty(u,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e}}),Object.defineProperty(u.people,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(){return u.toString(1)+".people (stub)"}}),o="init capture register register_once register_for_session unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group identify setPersonProperties setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags resetGroups onFeatureFlags addFeatureFlagsHandler onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
}
/* eslint-enable */

let started = false;

// Analytics must never stop the sign-up: these calls run before the app renders and in the middle of each step's
// success path, so a failure here is logged and dropped.
function safely(action: () => void) {
    try {
        action();
    } catch (error) {
        console.error('PostHog', error);
    }
}

// PostHog records the person's first touch with the first event of a browser that has none and sends it with
// $identify. Made on a sign-up page the site opened, its referrer is the site, and the visit's start is in that page's
// own address — whichever page load it was, this one or one before. Made on the site, it is left as it is.
function takeFirstTouchFromItsAddress(setOnce: Properties | undefined) {
    const url = setOnce?.$initial_current_url;
    if (!setOnce || typeof url !== 'string') return;
    const address = new URL(url);
    if (address.origin !== window.location.origin) return;
    const start = visitStart(address.search);
    if (!start) return;
    setOnce.$initial_referrer = start.$referrer;
    setOnce.$initial_referring_domain = start.$referring_domain;
}

// Called once, before the app renders: counts the sign-up as started for every visitor, wherever they came from.
export function startAnalytics() {
    const token = PROJECT_KEYS[window.location.hostname];
    if (started || !token) return;
    started = true;
    safely(() => {
        loadStub();
        // PostHog computes a session's channel from its first event's $referring_domain (and the campaign marks in the
        // address, which the site passes as they are); its entry page comes from $current_url, which stays this
        // page — the site's first page goes as site_landing_path. Checked on the Stage project, 03.11.2026.
        // Only the session PostHog starts for this page load's first event takes the visit's start: PostHog reports a
        // session it starts with a reason (none was there, or it had run out); one that goes on from the site's cookie
        // comes with none, and a new one after 30 idle minutes later on is not the visit's.
        const start = visitStart(window.location.search);
        let startedHere: string | undefined;
        let firstEventSeen = false;
        window.posthog?.init(token, {
            api_host: API_HOST,
            ui_host: 'https://eu.posthog.com',
            defaults: '2026-08-30',
            cross_subdomain_cookie: true,
            person_profiles: 'identified_only',
            disable_session_recording: true,
            loaded: (ph: LoadedPostHog) =>
                safely(() => {
                    ph.onSessionId((sessionId, _windowId, reason) => {
                        const started =
                            reason?.noSessionId || reason?.activityTimeout || reason?.sessionPastMaximumLength;
                        if (started && !firstEventSeen && !startedHere) startedHere = sessionId;
                    });
                }),
            before_send: (event: CapturedEvent) => {
                firstEventSeen = true;
                if (!event) return event;
                safely(() => takeFirstTouchFromItsAddress(event.$set_once));
                if (!start || !event.properties || event.properties.$session_id !== startedHere) return event;
                Object.assign(event.properties, start);
                // The session's entry as a person property, sent once with $identify, is the visit's start too.
                if (event.$set_once && '$referring_domain' in event.$set_once) {
                    event.$set_once.$referrer = start.$referrer;
                    event.$set_once.$referring_domain = start.$referring_domain;
                }
                return event;
            },
        });
        // A return to an unfinished sign-up: the same person. Its email came with step 1 (identifySignup) and is not
        // sent again: the draft's email may since be another one, from a ?email= link (moveEmailFromAddressToDraft).
        const uuid = storedSignupUuid();
        if (uuid) identifyAs(uuid);
        window.posthog?.capture('signup:flow_started');
    });
}

// Step 1 accepted: from here on the visitor is the sign-up (signupPersonId), with the email as a person property. The
// email is what the Google Ads destination sends, hashed, to match a trial whose ad click carried no gclid (Vova
// 30.10.2026); trimmed and lowercased, as Google matches hashes of the normalised address. Events sent before the id
// is ready go with the anonymous visitor and join the person with $identify.
export function identifySignup(uuid: string | undefined, email?: string) {
    const normalised = email?.trim().toLowerCase();
    if (uuid) identifyAs(uuid, normalised ? { email: normalised } : undefined);
}

// Every event goes at once, not in PostHog's batch that leaves every few seconds: the last ones come right before the
// browser moves on (the /complete/ page stays three seconds). Not by beacon: two beacons sent together lost one in
// testing (29.10.2026).
export function signupEvent(event: string, properties?: Properties) {
    safely(() => window.posthog?.capture(event, properties, { send_instantly: true }));
}

// The sign-up is over and the visitor is leaving /complete/ for the product: PostHog forgets the person, as the sign-up
// forgot its draft, so a second sign-up in the same browser is a new visitor, and neither the sign-up's id nor the
// email stays in the browser. Called right before the page goes, so the trial and /complete/ stay one person and one
// session; the page-leave event, which would go as the new visitor, is turned off first.
export function endSignupAnalytics() {
    // Before array.js has arrived (a reload of /complete/, which leaves at once) the stub has no set_config and only
    // queues reset(); a page gone before the library is left to the product, which resets a person it does not know.
    safely(() => {
        const ph = window.posthog as Partial<PostHog> | undefined;
        ph?.set_config?.({ capture_pageleave: false });
        ph?.reset?.();
    });
}
